import { PricingRow } from "../builderSchema";

export type OperatorType = '==' | '!=' | '>' | '<' | '>=' | '<=';
export type LogicalOperator = 'AND' | 'OR';

export interface Condition {
    id: string;
    dataType: string;
    attribute: string;
    operator: OperatorType;
    valueType: 'operand' | 'value';
    dataType2?: string;
    attribute2?: string;
    value?: string;
}

export interface ConditionGroup {
    id: string;
    type: 'group' | 'subgroup';
    conditions: (Condition | ConditionGroup)[];
    logicalOperators: LogicalOperator[];
}

export interface RuleStructure {
    groups: (ConditionGroup | Condition)[];
    groupOperators: LogicalOperator[];
}

export const generateId = (prefix: string) => `${prefix}-${Date.now()}-${Math.random()}`;

export const findGroupById = (items: any[], id: string): any => {
    for (const item of items) {
        if (item.id === id) return item;
        if (item.conditions) {
            const found = findGroupById(item.conditions, id);
            if (found) return found;
        }
    }
    return null;
};

export const findConditionById = (items: any[], id: string): any => {
    for (const item of items) {
        if (item.id === id && !item.conditions) return item;
        if (item.conditions) {
            const found = findConditionById(item.conditions, id);
            if (found) return found;
        }
    }
    return null;
};

//For Normal Rule
export const buildRuleStringFromStructure = (
    items: any[],
    operators: LogicalOperator[],
    level = 0
): string => {
    const indent = '    '.repeat(level);
    const lines: string[] = [];

    items?.forEach((item, index) => {

        if (index > 0 && operators[index - 1]) {
            lines.push(`${indent}${operators[index - 1]}`);
        }

        if (item.conditions) {
            lines.push(`${indent}(`);

            const inner = buildRuleStringFromStructure(
                item.conditions,
                item.logicalOperators,
                level + 1
            );
            lines.push(inner);

            lines.push(`${indent})`);
        } else {
            const left = item.dataType && item.attribute
                ? `${item.dataType}.${item.attribute}`
                : 'DataType1.Attribute1';

            const op = item.operator || '==';

            const right = item.valueType === 'value'
                ? (item.value || 'Value')
                : (item.dataType2 && item.attribute2
                    ? `${item.dataType2}.${item.attribute2}`
                    : 'DataType2.Attribute2');

            lines.push(`${indent}${left}${op}${right}`);
        }
    });

    return lines.join('\n');
};

class VariableMapper {
    private dataTypeMap: Map<string, string> = new Map();
    private attributeMap: Map<string, string> = new Map();
    private dataTypeCounter: number = 1;
    private attributeCounter: number = 1;

    getDataTypeVariable(dataType: string): string {
        if (!dataType) return '#DataType1#';

        if (!this.dataTypeMap.has(dataType)) {
            this.dataTypeMap.set(dataType, `#DataType${this.dataTypeCounter}#`);
            this.dataTypeCounter++;
        }
        return this.dataTypeMap.get(dataType)!;
    }

    getAttributeVariable(attribute: string): string {
        if (!attribute) return '[Attribute1]';

        if (!this.attributeMap.has(attribute)) {
            this.attributeMap.set(attribute, `[Attribute${this.attributeCounter}]`);
            this.attributeCounter++;
        }
        return this.attributeMap.get(attribute)!;
    }
}

// For AI String
export const buildFormattedRuleStringFromStructure = (
    items: any[],
    operators: LogicalOperator[],
    level = 0,
    mapper?: VariableMapper
): string => {
    const variableMapper = mapper || new VariableMapper();
    const indent = "  ".repeat(level);
    const indentInner = "  ".repeat(level + 1);

    return items?.map((item, index) => {
        let str = "";

        if (index > 0 && operators[index - 1]) {
            str += `$${operators[index - 1]}$`;
        }

        if (item.conditions) {
            const inner = buildFormattedRuleStringFromStructure(
                item.conditions,
                item.logicalOperators,
                level + 1,
                variableMapper
            );

            str += `(`;
            str += `${inner}`;
            str += `\n${indent})`;
        } else {
            const dataTypeVar = variableMapper.getDataTypeVariable(item.dataType);
            const attributeVar = variableMapper.getAttributeVariable(item.attribute);

            const left = `${dataTypeVar}.${attributeVar}`;
            const op = item.operator ? `@${item.operator}@` : "@==@";

            let right: string;
            if (item.valueType === "value") {
                right = item.value || "Value2";
            } else {
                const dt2 = variableMapper.getDataTypeVariable(item.dataType2);
                const attr2 = variableMapper.getAttributeVariable(item.attribute2);
                right = `${dt2}.${attr2}`;
            }

            str += `\n${indentInner}${left}${op}${right}`;
        }

        return str;
    }).join("");
};


const getRandomValue = () => Math.floor(Math.random() * 100) + 1;


// Create DT & ATTR Object for testing
export const extractDatatypesAndAttributes = (groups: any[]): Record<string, Record<string, number>> => {
    const result: Record<string, Record<string, number>> = {};

    const processCondition = (condition: Condition) => {
        if (condition.dataType && condition.attribute) {
            if (!result[condition.dataType]) {
                result[condition.dataType] = {};
            }
            if (!result[condition.dataType][condition.attribute]) {
                result[condition.dataType][condition.attribute] = getRandomValue();
            }
        }

        if (condition.valueType === 'operand' && condition.dataType2 && condition.attribute2) {
            if (!result[condition.dataType2]) {
                result[condition.dataType2] = {};
            }
            if (!result[condition.dataType2][condition.attribute2]) {
                result[condition.dataType2][condition.attribute2] = getRandomValue();
            }
        }
    };

    const traverseGroups = (items: any[]) => {
        items?.forEach(item => {
            if ('conditions' in item) {
                // It's a group or a subgroup
                traverseGroups(item.conditions);
            } else {
                // It's a condition
                processCondition(item);
            }
        });
    };

    traverseGroups(groups);
    return result;
};

// Builds test_data for Form Rules (type "6") from pricingRows

export function buildFormRuleTestData(
  pricingRows: PricingRow[]
): Record<string, Record<string, number>> {
  const testData: Record<string, Record<string, number>> = {};
  const valueMap: Record<string, number> = {};

  const getOrCreateValue = (key: string) => {
    if (!(key in valueMap)) {
      valueMap[key] = Math.floor(Math.random() * 100) + 1;
    }
    return valueMap[key];
  };

  for (const row of pricingRows) {
    if (row.isEditing) continue;

    // Left-hand side: dataType.attribute
    if (row.dataType && row.attribute) {
      const key = `${row.dataType}.${row.attribute}`;
      if (!testData[row.dataType]) testData[row.dataType] = {};
      testData[row.dataType][row.attribute] = getOrCreateValue(key);
    }

    // Right-hand side: only if expressionType is "operand" (dataType2.attribute2)
    if (row.expressionType === "operand" && row.dataType2 && row.attribute2) {
      const key = `${row.dataType2}.${row.attribute2}`;
      if (!testData[row.dataType2]) testData[row.dataType2] = {};
      testData[row.dataType2][row.attribute2] = getOrCreateValue(key);
    }
  }

  return testData;
}